import type { ToolAnswer, ToolContext, ToolDef } from '../mcp-types';
import { ACTION_TABLE, ActionField, ActionRow, Lane, secretFieldNames } from '../actions/action-table';
import { answerWithoutSecrets, wordsWithoutSecrets } from '../actions/secrets';
import { NO_PROJECT_REFUSAL } from './navigate.tool';

export type ActionHandler = (values: Record<string, unknown>, ctx: ToolContext, row: ActionRow) => Promise<ToolAnswer>;

/**
 * How one action runs, per lane. Only the row's lane is ever called, so an action that may move lanes supplies both.
 * A row with no handler for its lane is refused as not built, which is how a row reaches Claude before its behaviour does.
 */
export type ActionBehaviour = Partial<Record<Lane, ActionHandler>>;
export type ActionBehaviours = Record<string, ActionBehaviour>;

export interface DoActionDeps {
  behaviours: ActionBehaviours;
  /** The real table unless a check is moving a row. */
  table?: readonly ActionRow[];
}

function fieldSchema(field: ActionField): Record<string, unknown> {
  // A list's or an object's contents are the builder's data, and a usage record keeps only what is declared, so they stay undeclared.
  return field.type === 'array' ? { type: 'array', items: { type: 'object' } } : { type: field.type };
}

/** Every writable field in the table: a usage record drops what the schema doesn't declare. */
function valuesSchema(table: readonly ActionRow[]): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  for (const row of table) {
    for (const field of row.fields) if (!field.readOnly) properties[field.name] = fieldSchema(field);
  }
  return { type: 'object', description: 'The action\'s fields, by name, from read_screen.', properties };
}

export function makeDoActionTool(deps: DoActionDeps): ToolDef {
  const table = deps.table ?? ACTION_TABLE;
  const byId = new Map(table.map((row) => [row.id, row]));

  return {
    name: 'do_action',
    kind: 'waits',
    worksWithoutProject: true,
    recordDataPaths: secretFieldNames(table).map((name) => `values.${name}`),
    description: [
      'Takes one action from read_screen\'s list on the builder\'s screen, by its id, with its values.',
      'A fast-lane action runs and answers with the result. A confirm-lane action is prepared, and only the builder\'s click in Zavo commits it.',
    ].join(' '),
    inputSchema: {
      type: 'object',
      properties: {
        action: { type: 'string', enum: table.map((row) => row.id), description: 'The action\'s id, from read_screen.' },
        values: valuesSchema(table),
      },
      required: ['action'],
      additionalProperties: false,
    },

    async run(input: Record<string, unknown>, ctx: ToolContext): Promise<ToolAnswer> {
      const id = input['action'] as string;
      const values = (input['values'] ?? {}) as Record<string, unknown>;

      const row = byId.get(id);
      if (!row) return refused(`There is no action '${id}'. Nothing was changed; read_screen lists the actions on this screen.`);
      if (row.commit.kind === 'own-tool') return refused(`${row.label} is done by ${row.commit.tool}. Nothing was changed; call that instead.`);
      if (!ctx.projectId && !row.worksWithoutProject) return refused(NO_PROJECT_REFUSAL);

      const taken = row.fields.filter((f) => !f.readOnly).map((f) => f.name);
      const stray = Object.keys(values).find((name) => !taken.includes(name));
      if (stray !== undefined) {
        return refused(`${row.label} doesn't take "${stray}". Nothing was changed. ${taken.length ? `It takes: ${taken.join(', ')}.` : 'It takes no values.'}`);
      }

      const handler = deps.behaviours[row.id]?.[row.lane];
      if (!handler) return refused(row.whyNotBuilt ? `${row.whyNotBuilt} Nothing was changed.` : `${row.label} isn't built yet. Nothing was changed; tell the builder it has to be done by hand for now.`);
      try {
        return answerWithoutSecrets(await handler(values, ctx, row), values, row, table);
      } catch (err) {
        const said = err instanceof Error ? err.message : String(err);
        return refused(`${row.label} failed: ${wordsWithoutSecrets(said, values, row)}`);
      }
    },
  };
}

function refused(refusal: string): ToolAnswer {
  return { outcome: 'refused', refusal };
}
